<template>
  <form @submit.prevent="handleSignup" class="signup-form">
    <h2 class="signup-form__title">Create Account</h2>

    <div v-if="errorMessage" class="signup-form__error" role="alert">
      {{ errorMessage }}
    </div>

    <div v-if="successMessage" class="signup-form__success" role="status">
      {{ successMessage }}
    </div>

    <div class="signup-form__field">
      <label for="signup-name" class="signup-form__label">Full Name</label>
      <input
        id="signup-name"
        v-model="fullName"
        type="text"
        class="signup-form__input"
        placeholder="John Doe"
        required
        :disabled="isLoading"
        autocomplete="name"
      />
    </div>

    <div class="signup-form__field">
      <label for="signup-email" class="signup-form__label">Email</label>
      <input
        id="signup-email"
        v-model="email"
        type="email"
        class="signup-form__input"
        placeholder="you@example.com"
        required
        :disabled="isLoading"
        autocomplete="email"
      />
    </div>

    <div class="signup-form__field">
      <label for="signup-password" class="signup-form__label">Password</label>
      <input
        id="signup-password"
        v-model="password"
        type="password"
        class="signup-form__input"
        placeholder="At least 6 characters"
        required
        :disabled="isLoading"
        autocomplete="new-password"
        @input="validatePassword"
      />
      <div v-if="password" class="signup-form__password-strength">
        <div class="signup-form__strength-bar" :data-strength="passwordStrength"></div>
        <span class="signup-form__strength-text">{{ passwordStrengthText }}</span>
      </div>
    </div>

    <div class="signup-form__field">
      <label for="signup-confirm-password" class="signup-form__label">
        Confirm Password
      </label>
      <input
        id="signup-confirm-password"
        v-model="confirmPassword"
        type="password"
        class="signup-form__input"
        placeholder="Re-enter your password"
        required
        :disabled="isLoading"
        autocomplete="new-password"
      />
      <div v-if="confirmPassword && !passwordsMatch" class="signup-form__field-error">
        Passwords do not match
      </div>
    </div>

    <div class="signup-form__field-checkbox">
      <input
        id="signup-terms"
        v-model="agreedToTerms"
        type="checkbox"
        class="signup-form__checkbox"
        required
        :disabled="isLoading"
      />
      <label for="signup-terms" class="signup-form__checkbox-label">
        I agree to the
        <a href="/terms" target="_blank" class="signup-form__link">Terms of Service</a>
        and
        <a href="/privacy" target="_blank" class="signup-form__link">Privacy Policy</a>
      </label>
    </div>

    <button
      type="submit"
      class="signup-form__button signup-form__button--primary"
      :disabled="isLoading || !isFormValid"
    >
      {{ isLoading ? 'Creating account...' : 'Create Account' }}
    </button>

    <div class="signup-form__divider">
      <span>or</span>
    </div>

    <button
      type="button"
      @click="handleGoogleSignup"
      class="signup-form__button signup-form__button--google"
      :disabled="isLoading"
    >
      <svg class="signup-form__google-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Sign up with Google
    </button>

    <div class="signup-form__footer">
      <span class="signup-form__footer-text">Already have an account?</span>
      <router-link to="/login" class="signup-form__link">Sign in</router-link>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// Form state
const fullName = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const agreedToTerms = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// Loading state
const isLoading = computed(() => authStore.loading);

// Password validation
const passwordStrength = ref('weak');
const passwordStrengthText = ref('');

function validatePassword() {
  const pwd = password.value;
  
  if (pwd.length < 6) {
    passwordStrength.value = 'weak';
    passwordStrengthText.value = 'Too short';
  } else if (pwd.length < 8) {
    passwordStrength.value = 'fair';
    passwordStrengthText.value = 'Fair';
  } else if (pwd.length < 12 || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd)) {
    passwordStrength.value = 'good';
    passwordStrengthText.value = 'Good';
  } else {
    passwordStrength.value = 'strong';
    passwordStrengthText.value = 'Strong';
  }
}

// Check if passwords match
const passwordsMatch = computed(() => {
  return password.value === confirmPassword.value;
});

// Form validation
const isFormValid = computed(() => {
  return (
    fullName.value.trim() !== '' &&
    email.value.trim() !== '' &&
    password.value.length >= 6 &&
    passwordsMatch.value &&
    agreedToTerms.value
  );
});

const redirectTarget = computed(() => {
  const redirectQuery = route.query.redirect;
  if (typeof redirectQuery === 'string' && redirectQuery.startsWith('/')) {
    return redirectQuery;
  }
  return '/app';
});

function navigateToLogin(): void {
  if (typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/')) {
    router.push({
      path: '/login',
      query: { redirect: route.query.redirect },
    });
    return;
  }
  router.push('/login');
}

// Handle signup
async function handleSignup() {
  errorMessage.value = '';
  successMessage.value = '';

  if (!isFormValid.value) {
    errorMessage.value = 'Please fill in all fields correctly';
    return;
  }

  try {
    const { user, error } = await authStore.signUp(
      email.value,
      password.value,
      fullName.value
    );

    if (error) {
      errorMessage.value = error.message || 'Failed to create account';
      return;
    }

    // Success - show verification message
    successMessage.value = 
      'Account created! Please check your email to verify your account.';
    
    // Clear form
    fullName.value = '';
    email.value = '';
    password.value = '';
    confirmPassword.value = '';
    agreedToTerms.value = false;

    // Redirect to login after 3 seconds (preserve redirect intent if present)
    setTimeout(() => {
      navigateToLogin();
    }, 3000);

  } catch (err) {
    console.error('Signup error:', err);
    errorMessage.value = err.message || 'An unexpected error occurred';
  }
}

// Handle Google OAuth signup
async function handleGoogleSignup() {
  errorMessage.value = '';

  try {
    const { error } = await authStore.signInWithGoogle(redirectTarget.value);

    if (error) {
      errorMessage.value = error.message || 'Failed to sign up with Google';
    }

    // Browser will redirect to Google - no need to handle success here
  } catch (err) {
    console.error('Google signup error:', err);
    errorMessage.value = err.message || 'An unexpected error occurred';
  }
}
</script>

<style scoped>
.signup-form {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: var(--space-2xl);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
}

.signup-form__title {
  margin: 0 0 var(--space-xl) 0;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-align: center;
}

.signup-form__error {
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  background: #fee;
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.signup-form__success {
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  background: #efe;
  border: 1px solid var(--color-success);
  border-radius: var(--radius-md);
  color: var(--color-success);
  font-size: var(--font-size-sm);
}

.signup-form__field {
  margin-bottom: var(--space-lg);
}

.signup-form__label {
  display: block;
  margin-bottom: var(--space-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.signup-form__input {
  width: 100%;
  padding: var(--space-md);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  transition: border-color var(--transition-fast);
}

.signup-form__input:focus {
  outline: none;
  border-color: var(--color-accent);
}

.signup-form__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.signup-form__input::placeholder {
  color: var(--color-text-tertiary);
}

.signup-form__password-strength {
  margin-top: var(--space-sm);
}

.signup-form__strength-bar {
  height: 4px;
  background: var(--color-border-light);
  border-radius: var(--radius-full);
  position: relative;
  overflow: hidden;
}

.signup-form__strength-bar::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  transition: width var(--transition-fast), background var(--transition-fast);
}

.signup-form__strength-bar[data-strength="weak"]::after {
  width: 25%;
  background: var(--color-danger);
}

.signup-form__strength-bar[data-strength="fair"]::after {
  width: 50%;
  background: var(--color-warning);
}

.signup-form__strength-bar[data-strength="good"]::after {
  width: 75%;
  background: var(--color-info);
}

.signup-form__strength-bar[data-strength="strong"]::after {
  width: 100%;
  background: var(--color-success);
}

.signup-form__strength-text {
  display: block;
  margin-top: var(--space-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.signup-form__field-error {
  margin-top: var(--space-sm);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
}

.signup-form__field-checkbox {
  margin-bottom: var(--space-lg);
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
}

.signup-form__checkbox {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.signup-form__checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.signup-form__checkbox-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
}

.signup-form__button {
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
}

.signup-form__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.signup-form__button--primary {
  background: var(--color-accent);
  color: var(--color-burgundy-dark);
}

.signup-form__button--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.signup-form__button--google {
  margin-top: var(--space-md);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-medium);
}

.signup-form__button--google:hover:not(:disabled) {
  background: var(--color-bg-secondary);
}

.signup-form__google-icon {
  width: 20px;
  height: 20px;
}

.signup-form__divider {
  margin: var(--space-xl) 0;
  text-align: center;
  position: relative;
}

.signup-form__divider::before,
.signup-form__divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: calc(50% - var(--space-2xl));
  height: 1px;
  background: var(--color-border-light);
}

.signup-form__divider::before {
  left: 0;
}

.signup-form__divider::after {
  right: 0;
}

.signup-form__divider span {
  padding: 0 var(--space-md);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  background: var(--color-bg-primary);
}

.signup-form__footer {
  margin-top: var(--space-xl);
  text-align: center;
}

.signup-form__footer-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-right: var(--space-sm);
}

.signup-form__link {
  font-size: var(--font-size-sm);
  color: var(--color-accent);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.signup-form__link:hover {
  color: var(--color-accent-hover);
  text-decoration: underline;
}

.signup-form__link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}
</style>

